import { Card, Chip, IconButton } from "@mui/material";
import axios from "axios";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { server } from "../../server";
import {
    RiArrowLeftLine,
    RiShieldCheckFill,
    RiShieldFlashFill,
    RiMailLine,
    RiPhoneLine,
    RiEyeLine,
    RiShieldLine,
    RiFileTextFill
} from "react-icons/ri";
import { uploadDocumentToServer } from "../../utils/uploadDocumentToServer";

export default function UserDetails() {
    const navigate = useNavigate();
    const { state } = useLocation();
    const [loading, setLoading] = useState(true);
    const [userData, setUserData] = useState(null);
    const [verifyingId, setVerifyingId] = useState(null);

    const initialUser = state?.user;

    const getUserProfile = async (userId) => {
        try {
            setLoading(true);
            const { data } = await axios.get(`${server}/auth/getUserProfile`, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem("bvmToken")}`,
                },
                params: { userId }
            });
            setUserData(data?.data || null);
        } catch (e) {
            toast.error(e?.response?.data?.message || e?.message || "Failed to fetch user profile");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (initialUser?.id) {
            getUserProfile(initialUser.id);
        } else {
            navigate("../userList");
        }
    }, [initialUser, navigate]);



    const handleVerifyUser = async (userId) => {
        if (!window.confirm("Are you sure you want to verify this user?")) return;
        try {
            setVerifyingId(userId);
            const { data } = await axios.post(`${server}/auth/verifyUser`, null, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem("bvmToken")}`,
                },
                params: { userId },
            });
            toast.success(data?.message || "User verified successfully!");
            getUserProfile(userId);
        } catch (e) {
            toast.error(e?.response?.data?.message || e?.message || "Failed to verify user");
        } finally {
            setVerifyingId(null);
        }
    };

    const handleRejectUser = async (userId) => {
        const remarks = window.prompt("Enter Remarks for Rejection");
        if (!remarks) {
            toast.error("Remarks are required to reject a user");
            return;
        }
        if (!window.confirm("Are you sure you want to reject this user?")) return;
        try {
            setVerifyingId(userId);
            const { data } = await axios.post(`${server}/auth/rejectUser`, null, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem("bvmToken")}`,
                },
                params: { userId, remarks },
            });
            toast.success(data?.message || "User Rejected successfully!");
            getUserProfile(userId);
        } catch (e) {
            toast.error(e?.response?.data?.message || e?.message || "Failed to reject user");
        } finally {
            setVerifyingId(null);
        }
    };

    if (loading && !userData) {
        return (
            <div className="p-10 text-center space-y-4">
                <div className="animate-spin h-10 w-10 border-[4px] border-blue-600 border-t-transparent rounded-full mx-auto"></div>
                <p className="text-slate-500 font-bold">Loading Profile...</p>
            </div>
        );
    }


    return (
        <div className="p-4 lg:p-6 bg-[#fbfcfd] min-h-screen">
            <div className="max-w-5xl mx-auto space-y-6">
                {/* Navigation Header */}
                <div className="flex items-center gap-4">
                    <IconButton
                        onClick={() => navigate(-1)}
                        className="!bg-white !border !border-slate-200 !shadow-sm hover:!bg-slate-50 !transition-all"
                    >
                        <RiArrowLeftLine className="text-slate-600" />
                    </IconButton>
                    <div>
                        <h1 className="text-2xl font-bold text-slate-800 tracking-tight leading-none">User Profile</h1>
                        <p className="text-slate-500 text-xs font-bold mt-1 uppercase tracking-widest">Identity Details & Verification</p>
                    </div>
                </div>

                {/* Profile Overview Card */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <Card className="lg:col-span-1 p-6 rounded-2xl border border-slate-200/60 shadow-sm bg-white flex flex-col items-center text-center space-y-4">
                        <div className="relative">
                            <div className="w-24 h-24 rounded-3xl bg-gradient-to-tr from-blue-600 to-indigo-700 flex items-center justify-center text-white text-4xl  shadow-xl shadow-blue-500/20">
                                {userData?.name?.charAt(0) || "---"}
                            </div>
                            <div className={`absolute -bottom-2 -right-2 p-1.5 rounded-xl border-4 border-white shadow-lg ${userData?.verified ? "bg-green-500" : "bg-amber-500"}`}>
                                {userData?.verified ? <RiShieldCheckFill className="text-white text-lg" /> : <RiShieldFlashFill className="text-white text-lg" />}
                            </div>
                        </div>

                        <div className="space-y-1">
                            <h2 className="text-xl  text-slate-900 uppercase tracking-tight">{userData?.name}</h2>
                            <p className="text-sm font-bold text-blue-500 font-mono">{userData?.userName}</p>
                        </div>

                        <Chip
                            label={userData?.role?.toUpperCase() || "USER"}
                            size="small"
                            className="!bg-slate-100 !text-slate-600 ! !rounded-lg !px-2"
                        />

                        <div className="w-full pt-4 border-t border-slate-100 mt-4 space-y-3">
                            <div className="flex items-center justify-between text-xs font-bold">
                                <span className="text-slate-400 uppercase tracking-wider">Account Status</span>
                                <span className={userData?.verified ? "text-green-600" : "text-amber-600"}>
                                    {userData?.verified ? "VERIFIED" : "PENDING"}
                                </span>
                            </div>
                            <div className="flex items-center justify-between text-xs font-bold">
                                <span className="text-slate-400 uppercase tracking-wider">Security Level</span>
                                <span className="text-blue-600">ENCRYPTED</span>
                            </div>
                        </div>
                    </Card>

                    <Card className="lg:col-span-2 p-6 rounded-2xl border border-slate-200/60 shadow-sm bg-white divide-y divide-slate-50">
                        {/* Essential Info Grid */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pb-6">
                            <InfoItem icon={RiPhoneLine} label="Mobile Number" value={userData?.mobile || "Not Linked"} color="blue" />
                            <InfoItem icon={RiMailLine} label="Email Address" value={userData?.userName || "Not Provided"} color="indigo" />
                            {/* <InfoItem icon={RiFingerprintLine} label="Unique ID" value={`ID-${userData?.id}`} color="violet" /> */}
                            <InfoItem icon={RiShieldCheckFill} label="Identity Status" value={userData?.verified ? "Identity Verified" : "Verification Required"} color={userData?.verified ? "green" : "amber"} />
                            {/* <Button
                                variant="outlined"
                                onClick={() => setUploadModalOpen(true)}
                                sx={{
                                    borderRadius: 3,
                                    textTransform: 'none',
                                    fontWeight: 'bold',
                                    borderColor: '#e2e8f0',
                                    color: '#475569',
                                    '&:hover': {
                                        borderColor: '#2563eb',
                                        bgcolor: '#f8fafc'
                                    }
                                }}
                                className="w-full"
                            >
                                Update KYC Files
                            </Button> */}
                            <InfoItem icon={RiShieldCheckFill} label="Aadhaar Number" value={userData?.aadhaarNo} color={userData?.verified ? "green" : "amber"} />
                            <InfoItem icon={RiShieldCheckFill} label="Pan Number" value={userData?.panNo} color={userData?.verified ? "green" : "amber"} />

                        </div>

                        {/* Verification Actions */}
                        {!userData?.verified && (
                            <div className="pt-6">
                                <div className="bg-amber-50 border border-amber-100 rounded-xl p-4 flex items-start gap-4">
                                    <RiShieldFlashFill className="text-amber-500 text-3xl mt-1 shrink-0" />
                                    <div className="flex-1">
                                        <p className="text-amber-800  text-sm uppercase tracking-wide">Pending Authorization</p>
                                        <p className="text-amber-600/80 text-xs font-semibold mt-0.5">This user requires administrative review before profile activation.</p>
                                        <div className="flex gap-3 mt-4">
                                            <button
                                                onClick={() => handleRejectUser(userData.id)}
                                                disabled={verifyingId === userData.id}
                                                className="px-6 py-2 bg-white border border-amber-200 text-amber-600 text-xs  rounded-lg hover:bg-amber-100 transition-all disabled:opacity-50"
                                            >
                                                Reject Profile
                                            </button>
                                            <button
                                                onClick={() => handleVerifyUser(userData.id)}
                                                disabled={verifyingId === userData.id}
                                                className="px-8 py-2 bg-blue-600 text-white text-xs  rounded-lg hover:bg-blue-700 shadow-md shadow-blue-500/10 transition-all disabled:opacity-50"
                                            >
                                                {verifyingId === userData.id ? "Syncing..." : "Approve Now"}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </Card>
                </div>

                {/* Documents Section */}
                <div className="space-y-4">
                    <div className="flex items-center gap-2">
                        <RiShieldCheckFill className="text-blue-600 text-xl" />
                        <h3 className="text-sm  text-slate-800 uppercase tracking-[2px]">KYC Documents</h3>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <DocumentCard
                            label="Aadhaar Front Copy"
                            src={userData?.aadhaarCopyFront}
                            aadhaarNo={userData?.aadhaarNo}
                        />
                        <DocumentCard

                            label="Aadhaar Back Copy"
                            src={userData?.aadhaarCopyBack}
                            aadhaarNo={userData?.aadhaarNo}
                        />
                        <DocumentCard
                            label="Pan Card"
                            src={userData?.panCard}
                            aadhaarNo={userData?.panNo}
                        />
                        <DocumentCard
                            label="Character Certificate"
                            src={userData?.charecterCertificate}
                        // aadhaarNo={}
                        />
                    </div>
                </div>
            </div>

            {/* Upload Modal */}
            {/* <Modal open={uploadModalOpen} onClose={() => !uploading && setUploadModalOpen(false)}>
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: { xs: '94%', sm: 480 },
                    bgcolor: 'background.paper',
                    borderRadius: 5,
                    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.15)',
                    p: 4,
                }}>
                    <div className="space-y-6">
                        <div className="text-center">
                            <h3 className="text-xl font-bold text-slate-800">Update Profile KYC</h3>
                            <p className="text-slate-500 text-xs font-bold mt-1 uppercase tracking-widest">Aadhaar Card Upload</p>
                        </div>

                        <div className="space-y-4">
                            <div className="p-4 rounded-2xl bg-slate-50 border border-slate-100 space-y-3">
                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-wider block">Aadhaar Front Side</label>
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={(e) => setAadhaarFront(e.target.files[0])}
                                    className="w-full text-xs text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-xl file:border-0 file:text-[10px] file:font-black file:uppercase file:bg-blue-600 file:text-white hover:file:bg-blue-700 transition-all"
                                />
                                {aadhaarFront && <p className="text-[10px] font-bold text-blue-600">Selected: {aadhaarFront.name}</p>}
                            </div>

                            <div className="p-4 rounded-2xl bg-slate-50 border border-slate-100 space-y-3">
                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-wider block">Aadhaar Back Side</label>
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={(e) => setAadhaarBack(e.target.files[0])}
                                    className="w-full text-xs text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-xl file:border-0 file:text-[10px] file:font-black file:uppercase file:bg-blue-600 file:text-white hover:file:bg-blue-700 transition-all"
                                />
                                {aadhaarBack && <p className="text-[10px] font-bold text-blue-600">Selected: {aadhaarBack.name}</p>}
                            </div>
                        </div>

                        <div className="flex gap-3 pt-2">
                            <Button
                                fullWidth
                                onClick={() => setUploadModalOpen(false)}
                                disabled={uploading}
                                sx={{
                                    borderRadius: 3,
                                    textTransform: 'none',
                                    fontWeight: 'bold',
                                    color: '#64748b',
                                    bgcolor: '#f1f5f9',
                                    '&:hover': { bgcolor: '#e2e8f0' }
                                }}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="contained"
                                fullWidth
                                onClick={handleSubmitDocuments}
                                disabled={uploading}
                                sx={{
                                    borderRadius: 3,
                                    textTransform: 'none',
                                    fontWeight: 'bold',
                                    bgcolor: '#2563eb',
                                    boxShadow: '0 10px 15px -3px rgba(37, 99, 235, 0.2)',
                                    '&:hover': { bgcolor: '#1d4ed8' }
                                }}
                            >
                                {uploading ? "Uploading..." : "Save Documents"}
                            </Button>
                        </div>
                    </div>
                </Box>
            </Modal> */}
        </div>
    );
}

function InfoItem({ icon: Icon, label, value, color }) {
    return (
        <div className="flex items-center gap-4 group">
            <div className={`p-3 rounded-xl bg-${color}-50 text-${color}-600 group-hover:bg-${color}-100 transition-colors`}>
                <Icon className="text-xl" />
            </div>
            <div>
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-0.5">{label}</p>
                <p className="text-sm  text-slate-700">{value}</p>
            </div>
        </div>
    );
}

function DocumentCard({ label, src, aadhaarNo }) {
    const [showFull, setShowFull] = useState(false);

    return (
        <Card className="overflow-hidden rounded-2xl border border-slate-200 shadow-sm bg-white group">
            <div className="p-4 border-b border-slate-50 flex items-center justify-between">
                <div>
                    <p className="text-[10px]  text-slate-400 uppercase tracking-wider">{label}</p>
                    <p className="text-xs font-bold text-slate-600 mt-0.5">
                        {aadhaarNo}
                    </p>
                </div>
                <IconButton size="small" className="!bg-blue-50 !text-blue-600" onClick={() => setShowFull(!showFull)}>
                    <RiEyeLine />
                </IconButton>
            </div>
            <div className={`relative aspect-video bg-slate-50 flex items-center justify-center overflow-hidden ${src?.toLowerCase()?.includes(".pdf") ? "bg-red-50" : ""}`}>
                {src ? (
                    src.toLowerCase().includes(".pdf") ? (
                        <div className="flex flex-col items-center justify-center space-y-2">
                             <RiFileTextFill className="text-5xl text-red-500" />
                             <p className="text-[10px] font-bold text-red-600 uppercase tracking-widest">PDF Document</p>
                        </div>
                    ) : (
                        <img
                            src={src}
                            alt={label}
                            className={`w-full h-full object-fit transition-all duration-300 ${showFull ? "scale-100" : "scale-110 blur-[2px] opacity-40"}`}
                        />
                    )
                ) : (
                    <div className="text-center space-y-2">
                        <RiShieldLine className="text-3xl text-slate-300 mx-auto" />
                        <p className="text-[10px] font-bold text-slate-400 uppercase">Document Pending</p>
                    </div>
                )}
                {!showFull && src && !src.toLowerCase().includes(".pdf") && (
                    <div className="absolute inset-0 flex items-center justify-center">
                        <button
                            onClick={() => setShowFull(true)}
                            className="px-4 py-2 bg-white/90 backdrop-blur-sm text-blue-600 text-xs  rounded-lg shadow-lg border border-white hover:bg-white transition-all uppercase tracking-wide"
                        >
                            Click to Preview
                        </button>
                    </div>
                )}
                {src && src.toLowerCase().includes(".pdf") && (
                    <div className="absolute inset-0 flex items-center justify-center">
                        <button
                            onClick={() => window.open(src, "_blank")}
                            className="px-4 py-2 bg-white/90 backdrop-blur-sm text-red-600 text-xs  rounded-lg shadow-lg border border-white hover:bg-white transition-all uppercase tracking-wide"
                        >
                            Open PDF
                        </button>
                    </div>
                )}
            </div>
        </Card>
    );
}